/**
 * Ticket to Talk — pure TS score engine (no DOM).
 * Multi-class: Tech/General 35/26, Extra 50/37.
 */

import {
  type AttemptDraft,
  type AttemptResult,
  type ChoiceLetter,
  type DrillFilter,
  type PoolFile,
  type Question,
  DRILL_TOTAL,
  configForPool,
  groupsForPool,
  passScoreForPool,
} from "./types.js";

const LETTERS: ChoiceLetter[] = ["A", "B", "C", "D"];

/** Hash a seed string to a 32-bit unsigned int. */
export function hashSeed(seed: string): number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return h >>> 0;
}

/** Mulberry32 PRNG from a numeric seed. */
export function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function createRng(seed: string): () => number {
  return mulberry32(hashSeed(seed));
}

function shuffleInPlace<T>(arr: T[], rng: () => number): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

function activeQuestions(pool: PoolFile): Question[] {
  return pool.questions.filter((q) => q.withdrawn !== true);
}

function byId(pool: PoolFile): Map<string, Question> {
  const m = new Map<string, Question>();
  for (const q of activeQuestions(pool)) m.set(q.id, q);
  return m;
}

function pickOne<T>(items: T[], rng: () => number): T {
  if (items.length === 0) throw new Error("pickOne: empty list");
  return items[Math.floor(rng() * items.length)]!;
}

function makeChoiceMap(rng: () => number): ChoiceLetter[] {
  return shuffleInPlace([...LETTERS], rng);
}

function filterQuestions(pool: PoolFile, filter: DrillFilter): Question[] {
  const qs = activeQuestions(pool);
  switch (filter.kind) {
    case "subelement":
      return qs.filter((q) => q.subelement === filter.id);
    case "group":
      return qs.filter((q) => q.group === filter.id);
    case "tag":
      return qs.filter((q) => q.tags.includes(filter.tag));
    default: {
      const _exhaustive: never = filter;
      return _exhaustive;
    }
  }
}

/**
 * Full mock: exactly one question per class group list.
 * hintBudget 0. Choices shuffled; presentation order shuffled.
 * Withdrawn questions are excluded from selection.
 */
export function createMockAttempt(pool: PoolFile, seed: string): AttemptDraft {
  const rng = createRng(seed);
  const qs = activeQuestions(pool);
  const groups = groupsForPool(pool);
  const cfg = configForPool(pool);
  const byGroup = new Map<string, Question[]>();
  for (const q of qs) {
    const list = byGroup.get(q.group) ?? [];
    list.push(q);
    byGroup.set(q.group, list);
  }

  const picked: Question[] = [];
  for (const group of groups) {
    const list = byGroup.get(group);
    if (!list || list.length === 0) {
      throw new Error(`createMockAttempt: no active questions in group ${group}`);
    }
    picked.push(pickOne(list, rng));
  }

  shuffleInPlace(picked, rng);

  const items = picked.map((q, order) => ({
    questionId: q.id,
    order,
    choiceMap: makeChoiceMap(rng),
  }));

  if (items.length !== cfg.mockTotal) {
    throw new Error(
      `createMockAttempt: expected ${cfg.mockTotal} items, got ${items.length}`,
    );
  }

  return {
    mode: "full_mock",
    poolVersion: pool.poolVersion,
    revision: pool.revision,
    rngSeed: seed,
    items,
    hintBudget: 0,
  };
}

/**
 * Section drill: 10 questions from filter.
 * Prefer unique IDs; replace only after exhausting the slice.
 * hintBudget 1.
 */
export function createDrillAttempt(
  pool: PoolFile,
  filter: DrillFilter,
  seed: string,
): AttemptDraft {
  const rng = createRng(seed);
  const slice = filterQuestions(pool, filter);
  if (slice.length === 0) {
    throw new Error(`createDrillAttempt: empty filter ${JSON.stringify(filter)}`);
  }

  const picked: Question[] = [];
  const remaining = [...slice];
  shuffleInPlace(remaining, rng);

  while (picked.length < DRILL_TOTAL) {
    if (remaining.length === 0) {
      const refill = [...slice];
      shuffleInPlace(refill, rng);
      remaining.push(...refill);
    }
    picked.push(remaining.shift()!);
  }

  const items = picked.map((q, order) => ({
    questionId: q.id,
    order,
    choiceMap: makeChoiceMap(rng),
  }));

  return {
    mode: "section_drill",
    poolVersion: pool.poolVersion,
    revision: pool.revision,
    rngSeed: seed,
    items,
    hintBudget: 1,
  };
}

export function gradeQuestion(q: Question, selected: ChoiceLetter): boolean {
  return selected === q.answer;
}

export type SubmitOptions = {
  startedAt?: string;
  finishedAt?: string;
  hintUsedByQuestion?: Record<string, boolean>;
  sessionHintUsed?: boolean;
};

/**
 * Submit answers keyed by questionId → canonical pool letter.
 * Blanks (missing) count as incorrect.
 * selected/correct on result are always canonical pool letters.
 */
export function submitAttempt(
  draft: AttemptDraft,
  answers: Record<string, ChoiceLetter>,
  pool: PoolFile,
  opts: SubmitOptions = {},
): AttemptResult {
  const lookup = byId(pool);
  const cfg = configForPool(pool);
  const startedAt = opts.startedAt ?? new Date().toISOString();
  const finishedAt = opts.finishedAt ?? new Date().toISOString();
  const hintUsedByQuestion = opts.hintUsedByQuestion ?? {};
  let sessionHintUsed = opts.sessionHintUsed ?? false;

  const items = draft.items.map((item) => {
    const q = lookup.get(item.questionId);
    if (!q) throw new Error(`submitAttempt: unknown question ${item.questionId}`);
    const raw = answers[item.questionId];
    const selected: ChoiceLetter | null =
      raw === "A" || raw === "B" || raw === "C" || raw === "D" ? raw : null;
    const isCorrect = selected !== null && gradeQuestion(q, selected);
    const hintUsed = !!hintUsedByQuestion[item.questionId];
    if (hintUsed) sessionHintUsed = true;
    return {
      questionId: item.questionId,
      order: item.order,
      choiceMap: [...item.choiceMap] as ChoiceLetter[],
      selected,
      correct: q.answer,
      isCorrect,
      hintUsed,
    };
  });

  const score = items.filter((i) => i.isCorrect).length;
  const total = items.length;
  const passLine = passScoreForPool(pool);
  const passed = draft.mode === "full_mock" ? score >= passLine : null;

  return {
    mode: draft.mode,
    poolVersion: draft.poolVersion,
    revision: draft.revision,
    element: cfg.element,
    licenseClass: cfg.licenseClass,
    rngSeed: draft.rngSeed,
    startedAt,
    finishedAt,
    score,
    total,
    passed,
    hintUsed: sessionHintUsed,
    items,
  };
}

export function getQuestion(pool: PoolFile, id: string): Question | undefined {
  return byId(pool).get(id);
}

/** Active (non-withdrawn) group ids present in the pool. */
export function activeGroupsInPool(pool: PoolFile): Set<string> {
  const s = new Set<string>();
  for (const q of activeQuestions(pool)) s.add(q.group);
  return s;
}

/** Derive subelement ids from active questions, stable sort. */
export function subelementsFromPool(pool: PoolFile): string[] {
  const s = new Set<string>();
  for (const q of activeQuestions(pool)) s.add(q.subelement);
  return [...s].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}
